import {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useNavigate } from 'react-router-dom'
import { showConfirmAlert } from '_metronic/helpers/AlertHelper'
import { LocalVideoChamada, OrigemPipChamada } from '_metronic/helpers/enumsUI'
import { useT } from '_metronic/i18n/Metronici18n'
import { usePipAutomatico } from 'app/helpers/hooks/usePipAutomatico'
import { useInactivityContext } from 'app/modules/auth/inatividade/InactivityContext'
import { ChamadaAtivaHost, useHostDaChamada } from './ChamadaAtivaHost'
import { ChamadaAtivaContextValue, DadosChamada, rotaDaChamada } from './core/_models'

const ChamadaAtivaContext = createContext<ChamadaAtivaContextValue | undefined>(undefined)

/**
 * Mantém a chamada da teleconsulta viva enquanto o profissional navega (LO-829). Fica no PrivateRoutes,
 * ACIMA do SuspensedView: um subtree que volta a suspender tem refs desconectados (spec, seção 3).
 */
export const ChamadaAtivaProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [dados, setDados] = useState<DadosChamada | null>(null)
  const dadosRef = useRef<DadosChamada | null>(null)
  const [participantes, setParticipantes] = useState(1)
  const [chatAberto, setChatAberto] = useState(false)
  // Aviso "o vídeo foi fechado" aberto: o vídeo fica oculto (não vira mini-player) até o médico escolher.
  const [avisoPipFechado, setAvisoPipFechado] = useState(false)
  const avisoPipFechadoRef = useRef(false)
  const navigate = useNavigate()
  const { t } = useT()
  const { status } = useInactivityContext()
  const bloqueado = status === 'bloqueado'
  const ativo = dados !== null

  const fecharChat = useCallback(() => setChatAberto(false), [])
  // O host precisa do aviso antes de ele poder ser definido (depende de `desativar` do host).
  const avisarPipFechadoRef = useRef<() => void>(() => undefined)
  const aoFecharPipForaDaTela = useCallback(() => avisarPipFechadoRef.current(), [])
  const host = useHostDaChamada({
    ativo,
    aoSairDaTela: fecharChat,
    aoFecharPipForaDaTela,
  })
  const { devolverEFechar, desativar, abrirPip, naTela } = host
  const minimizar = useCallback(() => abrirPip(OrigemPipChamada.Gesto), [abrirPip])
  const abrirPipAutomatico = useCallback(() => abrirPip(OrigemPipChamada.Automatico), [abrirPip])

  // Troca de aba abre o PiP — nunca durante o bloqueio, para não reabrir o vídeo por cima dele.
  usePipAutomatico(ativo && !bloqueado, abrirPipAutomatico)

  // Bloqueio por inatividade (segurança/LGPD): o vídeo volta para baixo do modal de bloqueio.
  useEffect(() => {
    if (bloqueado) devolverEFechar()
  }, [bloqueado, devolverEFechar])

  // Logout / desmontagem: desativa, devolve o nó e fecha o PiP antes dos cleanups passivos do player
  // (contrato 3). Roda antes do detach do encaixe, que assim não tenta abrir PiP.
  useLayoutEffect(() => () => desativar(), [desativar])

  // Idempotente: com uma chamada já ativa (deste ou de outro atendimento) não faz nada — a tela decide o
  // que mostrar comparando `atendimentoId` do contexto com o seu.
  const iniciarChamada = useCallback((novos: DadosChamada) => {
    if (dadosRef.current !== null) return
    dadosRef.current = novos
    setDados(novos)
  }, [])

  /** Sai da videochamada (o player desmonta e deixa o canal). Não muda o estado do atendimento. */
  const sairDaChamada = useCallback(() => {
    desativar()
    dadosRef.current = null
    setDados(null)
    setChatAberto(false)
    setParticipantes(1)
  }, [desativar])

  /**
   * Leva o médico à tela da chamada, de qualquer tela. Já nela, só devolve o vídeo — navegar para a mesma
   * rota empilharia uma entrada repetida no histórico. Fora dela, o encaixe que monta fecha o PiP.
   */
  const irParaChamada = useCallback(
    (atendimentoId: number) => {
      devolverEFechar()
      if (!naTela()) navigate(rotaDaChamada(atendimentoId))
    },
    [devolverEFechar, naTela, navigate]
  )

  /**
   * "Voltar ao atendimento" do cabeçalho do PiP (substitui o "voltar à aba" nativo, que só fecha a janela).
   * `window.focus()` num clique dentro do PiP traz a aba de origem para a frente (Chrome 123+).
   */
  const voltarAoAtendimento = useCallback(() => {
    const chamada = dadosRef.current
    if (!chamada) return
    window.focus()
    irParaChamada(chamada.idAtendimento)
  }, [irParaChamada])

  /**
   * O médico fechou o PiP fora da tela do atendimento: pergunta se volta ou sai da chamada. "Voltar" é a
   * ação segura e também o que vale para Esc/clique fora — sair da chamada só por escolha explícita. Sair
   * não finaliza o atendimento.
   */
  const avisarPipFechado = useCallback(async () => {
    const chamada = dadosRef.current
    if (!chamada || avisoPipFechadoRef.current) return
    avisoPipFechadoRef.current = true
    setAvisoPipFechado(true)
    const sair = await showConfirmAlert({
      title: t('atendimento.chamada.aviso_fechado_titulo'),
      text: chamada.protocolo
        ? t('atendimento.chamada.aviso_fechado_texto_protocolo', {
            paciente: chamada.pacienteNome,
            protocolo: chamada.protocolo,
          })
        : t('atendimento.chamada.aviso_fechado_texto', { paciente: chamada.pacienteNome }),
      icon: 'warning',
      confirmButtonText: t('atendimento.chamada.sair'),
      cancelButtonText: t('atendimento.chamada.voltar'),
      emphasizeCancel: true,
    })
    avisoPipFechadoRef.current = false
    setAvisoPipFechado(false)
    // A chamada pode ter mudado enquanto o aviso estava aberto (ex.: encerrada por logout).
    if (dadosRef.current?.idAtendimento !== chamada.idAtendimento) return
    if (sair) sairDaChamada()
    else irParaChamada(chamada.idAtendimento)
  }, [t, sairDaChamada, irParaChamada])

  useLayoutEffect(() => {
    avisarPipFechadoRef.current = () => void avisarPipFechado()
  }, [avisarPipFechado])

  const encerrarChamada = useCallback(
    (atendimentoId: number) => {
      if (dadosRef.current?.idAtendimento !== atendimentoId) return
      sairDaChamada()
    },
    [sairDaChamada]
  )

  // Mesma conta que a tela do atendimento já fazia em `handleNewUser` (remotos + o próprio médico).
  const aoMudarParticipantes = useCallback(
    (quantidade: number) => setParticipantes(quantidade + 1),
    []
  )

  const value = useMemo<ChamadaAtivaContextValue>(
    () => ({
      atendimentoId: dados?.idAtendimento ?? null,
      localDoVideo: host.localDoVideo,
      pipSuportado: host.pipSuportado,
      participantes,
      chatAberto,
      setChatAberto,
      iniciarChamada,
      encerrarChamada,
      minimizar,
      restaurar: devolverEFechar,
      registrarEncaixe: host.registrarEncaixe,
    }),
    [
      dados,
      host.localDoVideo,
      host.pipSuportado,
      minimizar,
      devolverEFechar,
      host.registrarEncaixe,
      participantes,
      chatAberto,
      iniciarChamada,
      encerrarChamada,
    ]
  )

  return (
    <ChamadaAtivaContext.Provider value={value}>
      {children}
      <ChamadaAtivaHost
        no={host.no}
        cabecalhoPip={host.cabecalhoPip}
        dados={dados}
        chatAberto={chatAberto}
        setChatAberto={setChatAberto}
        aoMudarParticipantes={aoMudarParticipantes}
        miniPlayerVisivel={
          ativo && host.localDoVideo === LocalVideoChamada.Mini && !avisoPipFechado
        }
        registrarMini={host.registrarMini}
        aoVoltar={voltarAoAtendimento}
      />
    </ChamadaAtivaContext.Provider>
  )
}

/**
 * Variante que não exige o provider: devolve `undefined` fora da área logada (ex.: o paciente em
 * `/tele-consulta`). Usada pelos controles do player, que também renderizam fora do PrivateRoutes.
 */
export const useChamadaAtivaOpcional = () => useContext(ChamadaAtivaContext)

export const useChamadaAtiva = () => {
  const contexto = useContext(ChamadaAtivaContext)
  if (!contexto) {
    throw new Error('useChamadaAtiva deve ser usado dentro de um ChamadaAtivaProvider')
  }
  return contexto
}
